import { formatPlaceTitle } from '@/utils/formatPlaceTitle';

/**
 * Detail itinerary wiring.
 *
 * GET /api/cruises/:id (and /prices cruise_info) already returns:
 *   itinerary_schedule — list of {day, port, arrive, depart} (times may be null)
 *   ports_of_call — list of strings
 *   itinerary_details — JSON string of metadata (name, destination, images, …)
 *     or, on older rows, a plain sentence or a JSON string of the list/object.
 *
 * JSON.parse on an array stringifies it first ("Hong Kong,…" / "[object Object]"),
 * which is the console SyntaxError. Accept a list or one JSON string. Never
 * invent a port, a time, or a prose overview from the metadata blob.
 *
 * Carnival lead-sailing rows store clock times as ISO strings with a +00:00
 * suffix (local port time, not a UTC conversion) plus arriveTimeHMmTt /
 * departTimeHMmTt. Those strings must render as clock times. The date on
 * that schedule is the lead sailing only — show it when it matches the
 * selected sail, and omit it otherwise.
 */

const MISSING_TIME = /^(not specified|n\/a|na|null|none|unknown|-|—)$/i;

export function parseStructured(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const first = trimmed[0];
  if (first !== '{' && first !== '[' && first !== '"') return trimmed;
  try {
    let parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') {
      const inner = parsed.trim();
      if (inner.startsWith('{') || inner.startsWith('[')) {
        try {
          parsed = JSON.parse(inner);
        } catch (e) {
          /* keep the inner string */
        }
      }
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

const ISO_DATETIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
const RAW_ISO = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function normalizeClockText(value) {
  return String(value).trim().replace(/[\u202f\u00a0]/g, ' ');
}

/**
 * CP-222: Royal Caribbean Group publishes reliable 24-hour port clocks.
 * Only these lines convert bare HH:MM to 12-hour; other lines stay unchanged.
 */
export function usesReliable24HourPortTimes(cruiseLine) {
  const key = String(cruiseLine || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!key) return false;
  if (key === 'royal caribbean' || key.startsWith('royal caribbean ')) return true;
  if (key === 'celebrity' || key === 'celebrity cruises' || key.startsWith('celebrity cruises')) return true;
  return false;
}

/** Wall-clock 12-hour label. The offset on Carnival stamps is not a real zone. */
function wallClock12(hh, mm, ss) {
  const hour = Number(hh);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return '';
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'AM' : 'PM';
  if (ss && ss !== '00') return `${hour12}:${mm}:${ss} ${suffix}`;
  return `${hour12}:${mm} ${suffix}`;
}

/**
 * Format a source clock for Detail.
 * @param {*} value
 * @param {{ as12Hour?: boolean }} [options]  When true (RCI / Celebrity only),
 *   bare 24-hour HH:MM(:SS) becomes 12-hour. Unreliable / ambiguous values are
 *   left as-is or blank — never guessed.
 */
export function formatItineraryTime(value, options) {
  if (value == null) return '';
  const as12Hour = Boolean(options && options.as12Hour);
  const s = normalizeClockText(value);
  if (!s || MISSING_TIME.test(s)) return '';

  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])$/);
  if (ampm) {
    const hour = Number(ampm[1]);
    if (hour < 1 || hour > 12) return '';
    const suffix = ampm[4].toUpperCase();
    if (ampm[3] && ampm[3] !== '00') return `${hour}:${ampm[2]}:${ampm[3]} ${suffix}`;
    return `${hour}:${ampm[2]} ${suffix}`;
  }

  const iso = s.match(ISO_DATETIME);
  if (iso) return wallClock12(iso[2], iso[3], iso[4]);

  const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    const hh = match[1].padStart(2, '0');
    const mm = match[2];
    const ss = match[3];
    if (as12Hour) return wallClock12(hh, mm, ss);
    if (ss && ss !== '00') return `${hh}:${mm}:${ss}`;
    return `${hh}:${mm}`;
  }

  // Never surface an unparsed timestamp. A short non-time label can pass through.
  if (RAW_ISO.test(s)) return '';
  return s;
}

/**
 * Date-only Y-M-D from a sail / schedule stamp. Takes the calendar digits from
 * the string (before any T/space). Never Date.parse a bare ISO date — in
 * America/New_York that would be UTC midnight and shift to the previous day.
 */
function calendarYmd(value) {
  if (value == null || value === '') return '';
  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  if (!match) return '';
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${match[1]}-${match[2]}-${match[3]}`;
}

/** Civil-date add using UTC Y-M-D parts only (timezone-independent). */
function addDaysYmd(ymd, days) {
  const match = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + days);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Human label for a civil Y-M-D (UTC calendar, not local midnight). */
function formatCalendarDate(ymd) {
  const match = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

function clockFrom(as12Hour, ...values) {
  for (const value of values) {
    if (value == null || value === '') continue;
    const formatted = formatItineraryTime(value, { as12Hour });
    if (formatted) return formatted;
  }
  return '';
}

function displayProse(value) {
  const text = portString(value);
  if (!text || RAW_ISO.test(text)) return '';
  return text;
}

function portString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

export function parsePortsOfCall(value) {
  const parsed = parseStructured(value);
  if (!Array.isArray(parsed)) return [];
  const ports = [];
  parsed.forEach((item) => {
    const name = portString(item);
    if (name) ports.push(name);
  });
  return ports;
}

function coerceDay(raw) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
}

function normalizeDay(entry, index, as12Hour) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const port = portString(entry.port) || portString(entry.location) || portString(entry.port_name);
  // Carnival stores a human clock (arriveTimeHMmTt) and the same instant as a
  // UTC-labeled ISO string. Prefer the human clock. Never keep the ISO string.
  const arrive = clockFrom(
    as12Hour,
    entry.arriveTimeHMmTt,
    entry.arrive,
    entry.arrival,
    entry.arrival_time,
    entry.arrivalTime
  );
  const depart = clockFrom(
    as12Hour,
    entry.departTimeHMmTt,
    entry.depart,
    entry.departure,
    entry.departure_time,
    entry.departureTime
  );
  const hasDay = (entry.day != null && entry.day !== '')
    || (entry.number != null && entry.number !== '')
    || (entry.day_number != null && entry.day_number !== '');
  if (!port && !arrive && !depart && !hasDay) return null;
  const day = hasDay
    ? coerceDay(entry.day ?? entry.number ?? entry.day_number)
    : index + 1;
  const location = portString(entry.location);
  const calendarDate = calendarYmd(entry.date)
    || calendarYmd(entry.arrive)
    || calendarYmd(entry.depart)
    || '';
  return {
    day,
    port: port || null,
    location: location || null,
    arrive: arrive || null,
    depart: depart || null,
    arrival: arrive || null,
    departure: depart || null,
    // Raw calendar stamp stays internal until it is checked against the sail.
    calendarDate: calendarDate || null,
    date: null,
    description: displayProse(entry.description) || null,
  };
}

export function parseItinerarySchedule(value, options) {
  const as12Hour = Boolean(options && options.as12Hour);
  const parsed = parseStructured(value);
  if (!Array.isArray(parsed)) return [];
  const days = [];
  parsed.forEach((entry, index) => {
    const day = normalizeDay(entry, index, as12Hour);
    if (day) days.push(day);
  });
  return days;
}

export function itineraryDayLabel(day, index) {
  const n = day && day.day != null && day.day !== '' ? day.day : index + 1;
  return `Day ${n}`;
}

/** Visible copy when a sailing has no day/port schedule. */
export const ITINERARY_UNAVAILABLE = 'Itinerary not published';

/** Shown when the stored day list belongs to a different sailing than the one selected. */
export const ITINERARY_TEMPLATE_NOTE = 'Port days and times are from the published itinerary. Calendar dates are not shown because they do not match the sailing you selected.';

/**
 * Scope calendar dates onto the selected sail (CP-214 / CP-223).
 *
 * 1. When schedule rows already carry dates that line up with the selected
 *    sail (Carnival lead sailing), keep those dates.
 * 2. When rows carry dates for a *different* sailing (template), drop the
 *    dates and keep ports/times — never shift them onto this sail.
 * 3. CP-223: when rows have only day/port/times (typical RCI / Celebrity
 *    per-sailing schedules) and the sailing has a real departure date,
 *    derive Day N as departure_date + (N - 1). No departure date → Day N only.
 */
function dayOffset(day) {
  const dayNumber = Number(day && day.day);
  return Number.isFinite(dayNumber) ? dayNumber - 1 : 0;
}

function scopeItineraryToSail(days, sailDate) {
  const sailYmd = calendarYmd(sailDate);
  const dated = days.filter((day) => day.calendarDate);
  const matches = Boolean(sailYmd) && dated.length > 0 && dated.every((day) => (
    day.calendarDate === addDaysYmd(sailYmd, dayOffset(day))
  ));
  const templateOnly = dated.length > 0 && !matches;
  // Derive only when nothing on the schedule is dated and we have a real sail.
  const deriveFromSail = Boolean(sailYmd) && dated.length === 0;
  const scoped = days.map((day) => {
    const next = { ...day };
    const calendarDate = next.calendarDate;
    delete next.calendarDate;
    let ymd = null;
    if (matches && calendarDate) {
      ymd = calendarDate;
    } else if (deriveFromSail) {
      ymd = addDaysYmd(sailYmd, dayOffset(next)) || null;
    }
    next.date = ymd ? formatCalendarDate(ymd) : null;
    next.dateYmd = ymd;
    return next;
  });
  return { days: scoped, templateOnly };
}

/** Visible when day-by-day is missing but the title already names ports. */
export const ITINERARY_TITLE_FALLBACK_NOTE =
  'Port names summarized from the cruise title. Day-by-day schedule and times are not available.';

/** Visible when day-by-day is missing but the cruise has stored port names (CP-165). */
export const ITINERARY_PORTS_FALLBACK_NOTE =
  'Port names only. Day-by-day schedule and times are not available.';

const SEA_DAY_PORT = /\b(fun day at sea|at sea|sea day)\b/i;
const SCENIC_PORT = /^(cruise\s|scenic cruising\b)|\bscenic cruising\b/i;

/** Scenic stretches ("Scenic Cruising Glacier Bay") are not port calls. */
export function isScenicCruisingName(value) {
  return SCENIC_PORT.test(portString(value));
}

/**
 * Port names for the Ports of Call card when ports_of_call is empty but the
 * day-by-day schedule exists (e.g. Carnival rows before CP-165 backfill).
 * Order kept, sea days and scenic stretches dropped, each port once.
 */
export function portsFromScheduleDays(days) {
  const ports = [];
  const seen = new Set();
  (Array.isArray(days) ? days : []).forEach((day) => {
    const name = portString(day && day.port);
    if (!name || SEA_DAY_PORT.test(name) || SCENIC_PORT.test(name)) return;
    const key = foldTitleToken(name);
    if (seen.has(key)) return;
    seen.add(key);
    ports.push(name);
  });
  return ports;
}

/**
 * Destination / region labels are not port calls. A title that only names these
 * stays empty. Mixed with a real place (e.g. "St. Maarten, Antigua & Bahamas")
 * the region token may still appear in the title summary.
 */
const TITLE_REGION_ONLY = new Set([
  'alaska',
  'asia',
  'australia',
  'australia & new zealand',
  'australia and new zealand',
  'bahamas',
  'bermuda',
  'canada & new england',
  'canada and new england',
  'caribbean',
  'eastern caribbean',
  'eastern mediterranean',
  'europe',
  'hawaii',
  'mediterranean',
  'mexican riviera',
  'mexico',
  'new zealand',
  'northern europe',
  'pacific coast',
  'pacific coastal',
  'pacific northwest',
  'panama canal',
  'south america',
  'south pacific',
  'southern caribbean',
  'the bahamas',
  'the caribbean',
  'transatlantic',
  'western caribbean',
  'western mediterranean',
  'world & exotic',
  'world and exotic',
]);

function foldTitleToken(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function isTitleRegionOnly(value) {
  return TITLE_REGION_ONLY.has(foldTitleToken(value));
}

function splitTitlePortList(value) {
  return String(value || '')
    .split(/\s*(?:,|&|\band\b)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);
}

function cleanTitlePortSegment(value) {
  let s = String(value || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  // Drop trailing schedule fluff that is not a place name.
  s = s.replace(/\s+overnight\b\.?$/i, '').trim();
  s = s.replace(/\s+cruise\b\.?$/i, '').trim();
  if (!s) return '';
  if (/^\d+[\s-]*(day|days|night|nights)\b/i.test(s)) return '';
  if (/^(day|night|nights|days|cruise|round[\s-]?trip)$/i.test(s)) return '';
  // Prefer the facility name inside "City (Port Name)" when present.
  const paren = s.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (paren) {
    const outer = paren[1].trim();
    const inner = paren[2].trim();
    if (/\bport\b/i.test(inner) || inner.length >= outer.length) {
      s = inner;
    }
  }
  // Display-normalize place casing (acronyms + ampersands). CP-137.
  return formatPlaceTitle(s);
}

function dedupeTitlePorts(ports) {
  const out = [];
  const seen = new Set();
  ports.forEach((port) => {
    const key = foldTitleToken(port);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(port);
  });
  return out;
}

/**
 * Honest port-name list from a cruise title that already names places.
 * Only colon-delimited titles qualify (prefix: place, place & place). No day
 * numbers, sea days, or times. Region-only titles stay empty.
 */
export function extractPortsFromTitle(title) {
  const text = String(title || '').replace(/\s+/g, ' ').trim();
  if (!text) return [];
  const colon = text.indexOf(':');
  if (colon === -1) return [];

  const prefix = text.slice(0, colon).trim();
  const suffix = text.slice(colon + 1).trim();
  if (!suffix) return [];

  const ports = [];

  // Embark named before the colon ("from X" / "Round-Trip X") is already on the title.
  const embarkMatch = prefix.match(/\b(?:from|round[\s-]?trip)\s+(.+)$/i);
  if (embarkMatch) {
    const embark = cleanTitlePortSegment(embarkMatch[1]);
    if (embark && !isTitleRegionOnly(embark)) ports.push(embark);
  }

  splitTitlePortList(suffix).forEach((part) => {
    const cleaned = cleanTitlePortSegment(part);
    if (cleaned) ports.push(cleaned);
  });

  const unique = dedupeTitlePorts(ports);
  // Need at least one non-region place so "Caribbean & Bahamas" after a colon
  // alone does not become a fake itinerary.
  if (!unique.some((port) => !isTitleRegionOnly(port))) return [];
  return unique;
}

/**
 * Detail always mounts an Itinerary section.
 * Days are only the parsed schedule. A missing or empty schedule may fall
 * back to port names already printed in the cruise title — never invent day
 * numbers, order beyond the title, sea days, or times.
 * sailDate scopes calendar dates; it does not invent ports or a new schedule.
 * title is optional; only used when the day-by-day schedule is missing.
 * portsValue (ports_of_call) is the next fallback; "Itinerary not published"
 * is only returned when there is no schedule, no title ports and no stored ports.
 * cruiseLine (CP-222): Royal Caribbean / Celebrity convert reliable 24-hour
 * port clocks to 12-hour; other lines keep the source format.
 */
export function buildItinerarySection(scheduleValue, sailDate, title, portsValue, cruiseLine) {
  const as12Hour = usesReliable24HourPortTimes(cruiseLine);
  const parsed = parseItinerarySchedule(scheduleValue, { as12Hour });
  const scoped = scopeItineraryToSail(parsed, sailDate);
  const available = scoped.days.length > 0;
  if (available) {
    return {
      mounted: true,
      available: true,
      days: scoped.days,
      emptyMessage: '',
      templateOnly: scoped.templateOnly,
      templateNote: scoped.templateOnly ? ITINERARY_TEMPLATE_NOTE : '',
      titlePorts: [],
      titleFallbackNote: '',
    };
  }

  const titlePorts = extractPortsFromTitle(title);
  if (titlePorts.length > 0) {
    return {
      mounted: true,
      available: false,
      days: [],
      emptyMessage: '',
      templateOnly: false,
      templateNote: '',
      titlePorts,
      titleFallbackNote: ITINERARY_TITLE_FALLBACK_NOTE,
    };
  }

  const storedPorts = parsePortsOfCall(portsValue);
  if (storedPorts.length > 0) {
    return {
      mounted: true,
      available: false,
      days: [],
      emptyMessage: '',
      templateOnly: false,
      templateNote: '',
      titlePorts: storedPorts,
      titleFallbackNote: ITINERARY_PORTS_FALLBACK_NOTE,
    };
  }

  return {
    mounted: true,
    available: false,
    days: [],
    emptyMessage: ITINERARY_UNAVAILABLE,
    templateOnly: false,
    templateNote: '',
    titlePorts: [],
    titleFallbackNote: '',
  };
}

export function itineraryOverviewProse(value) {
  const parsed = parseStructured(value);
  if (typeof parsed === 'string') {
    const text = parsed.trim();
    if (!text || text[0] === '{' || text[0] === '[') return '';
    return text;
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const desc = parsed.description;
    if (typeof desc === 'string') {
      const text = desc.trim();
      if (text && text[0] !== '{' && text[0] !== '[') return text;
    }
  }
  return '';
}
